/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { FormattedMessage } from 'react-intl'
import { useData } from 'vike-react/useData'
import { usePageContext } from 'vike-react/usePageContext'

import type { SanityTextContentProps } from '@slite/components'
import { SanityTextRenderer, SuperSourceIcon } from '@slite/components'
import { ArrowLeftIcon } from '@slite/icons'
import { SuperSource } from '@slite/search-client'

import { Button } from '../../../components/Button/Button'
import { Section } from '../../../components/Section'
import { SlitePage } from '../../../components/SlitePage'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { WorkflowCard } from '../WorkflowCard'
import type { Data } from './+data'
import { WorkflowStep } from './WorkflowStep'

const WORKFLOW_STEPS_INDENTATION_VALUES = [0, 32, 48]

const TRIGGER_TOOLS = new Set([
  'slack',
  'bulk',
  'extension',
  'buttons',
  'zapier',
])

const TOOL_LABELS: Record<string, string> = {
  slack: 'Slack',
  bulk: 'Bulk mode',
  extension: 'Chrome extension',
  buttons: 'Contextual buttons',
  n8n: 'n8n',
  zapier: 'Zapier',
  digests: 'Digests',
  api: 'API',
  mcp: 'MCP',
}

const isSuperSource = (value: string): value is SuperSource =>
  Object.values(SuperSource).includes(value as SuperSource)

function ToolPill({ tool }: { tool: string }) {
  const label = TOOL_LABELS[tool] ?? tool
  return (
    <div className="padding-vertical-12 padding-horizontal-16 border-small border-alpha-shade-40 radius-larger background-white flexbox flexbox-align-center flexbox-column-gap-8">
      <span className="font-weight-semibold shade-05">{label}</span>
    </div>
  )
}

export default function WorkflowShowPage() {
  const routes = useRoutes()
  const { workflow } = useData<Data>()
  const pageContext = usePageContext()
  const slug = pageContext.routeParams.slug
  const workflowContent = workflow.article as SanityTextContentProps
  const similarWorkflows = workflow.similarWorkflows

  const pageTitle = `${workflow.title} | Slite`
  const pageDescription = workflow.description

  const triggerTools = workflow.tools.filter((tool) => TRIGGER_TOOLS.has(tool))
  const resultTools = workflow.tools.filter((tool) => !TRIGGER_TOOLS.has(tool))

  const tryInSuperUrl = `https://super.slite.com/api/redirect/super/assistants/draft?fromTemplate=${workflow._id}`

  const validSources = workflow.sources.filter(isSuperSource)

  return (
    <SlitePage
      title={pageTitle}
      description={pageDescription}
      canonicalUrl={routes.workflows.detail({ slug })}
      background="moon-dust-lighter"
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/d3f53a2d5decdbb5797fb8f001995241ebb4c3f5-1200x630.jpg"
    >
      <Section background="moon-dust-lighter">
        <div className="grid-12 grid-gap-40">
          <aside className="grid-span-4 grid-span-12-medium grid-span-12-small">
            <div
              className="position-sticky"
              css={css`
                top: 100px;
              `}
            >
              <a
                href={routes.workflows.index()}
                className="flexbox flexbox-align-center flexbox-column-gap-8 shade-30 shade-10-on-hover margin-bottom-40 font-weight-semibold"
                style={{ textDecoration: 'none' }}
              >
                <ArrowLeftIcon size={14} />
                <span className="font-size-9">
                  <FormattedMessage
                    id="workflows.detail.back"
                    defaultMessage="Back to workflows"
                  />
                </span>
              </a>

              <WebsiteText
                variant="title"
                htmlTag="h1"
                className="margin-bottom-24"
              >
                {workflow.title}
              </WebsiteText>

              <Button
                href={tryInSuperUrl}
                variant="primary"
                size="huge"
                target="_blank"
                rel="noopener noreferrer"
                className="width-full margin-bottom-40"
              >
                <FormattedMessage
                  id="workflows.detail.tryIt"
                  defaultMessage="Try in Slite"
                />
              </Button>

              <div className="margin-bottom-32">
                <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-8 flexbox-inline">
                  <FormattedMessage
                    id="workflows.detail.givesYou"
                    defaultMessage="Gives you"
                  />
                </span>
                <p className="font-size-6 shade-05">{workflow.description}</p>
              </div>

              {workflow.personas.length > 0 && (
                <div className="margin-bottom-32">
                  <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-8 flexbox-inline">
                    <FormattedMessage
                      id="workflows.detail.perfectFor"
                      defaultMessage="Perfect for"
                    />
                  </span>
                  <div
                    className="flexbox flexbox-align-center flexbox-column-gap-12 flexbox-wrap"
                    css={css`
                      span:last-child {
                        display: none;
                      }
                    `}
                  >
                    {workflow.personas.map((persona) => (
                      <React.Fragment key={persona.name}>
                        <span className="font-size-6 shade-05">
                          {persona.name}
                        </span>
                        <span
                          className="radius-round flexbox-no-shrink background-alpha-shade-40"
                          css={css`
                            width: 3px;
                            height: 3px;
                          `}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              {validSources.length > 0 && (
                <div>
                  <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-8 flexbox-inline">
                    <FormattedMessage
                      id="workflows.detail.useWith"
                      defaultMessage="Use with"
                    />
                  </span>
                  <div className="flexbox flexbox-align-center flexbox-column-gap-8 flexbox-wrap">
                    {validSources.map((source) => (
                      <SuperSourceIcon
                        key={source}
                        size={24}
                        sourceType={source}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>

          <section className="grid-span-8 grid-span-12-medium grid-span-12-small">
            {triggerTools.length > 0 && (
              <div className="margin-bottom-32">
                <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-16 flexbox-inline">
                  <FormattedMessage
                    id="workflows.detail.trigger"
                    defaultMessage="Trigger this workflow"
                  />
                </span>
                <div className="flexbox flexbox-column flexbox-row-gap-12">
                  {triggerTools.map((tool) => (
                    <ToolPill key={tool} tool={tool} />
                  ))}
                </div>
              </div>
            )}

            <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-16 flexbox-inline">
              <FormattedMessage
                id="workflows.detail.steps"
                defaultMessage="Steps"
              />
            </span>
            <div className="flexbox flexbox-column flexbox-row-gap-12 margin-bottom-32">
              {workflow.steps.map((step, index) => (
                <div
                  key={`${step.title}-${index}`}
                  css={css`
                    padding-left: ${
                      WORKFLOW_STEPS_INDENTATION_VALUES[step.indentation]
                    }px;
                  `}
                >
                  <WorkflowStep title={step.title} type={step.type}>
                    {step.description && (
                      <span className="display-none-small display-none-medium">
                        {step.description}
                      </span>
                    )}
                  </WorkflowStep>
                </div>
              ))}
            </div>

            {resultTools.length > 0 && (
              <div className="margin-bottom-32">
                <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-16 flexbox-inline">
                  <FormattedMessage
                    id="workflows.detail.result"
                    defaultMessage="Get the workflow result"
                  />
                </span>
                <div className="flexbox flexbox-column flexbox-row-gap-12">
                  {resultTools.map((tool) => (
                    <ToolPill key={tool} tool={tool} />
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </Section>

      {workflowContent && workflowContent.length > 0 && (
        <Section background="moon-dust-lighter" containerTopPadding={0}>
          <div className="grid-12">
            <div className="grid-span-8 grid-offset-2 grid-span-12-medium grid-offset-0-medium grid-span-12-small grid-offset-0-small">
              <span className="font-size-9 font-weight-semibold shade-30 margin-bottom-16 flexbox-inline">
                <FormattedMessage
                  id="workflows.detail.instructions"
                  defaultMessage="Instructions"
                />
              </span>
              <div className="simple-rich-text simple-rich-text-sanity">
                <SanityTextRenderer
                  variant="website"
                  content={workflowContent}
                />
              </div>
            </div>
          </div>
        </Section>
      )}

      {similarWorkflows && similarWorkflows.length > 0 && (
        <Section background="moon-dust-lighter" containerTopPadding={0}>
          <div className="grid-12 grid-gap-40">
            <div className="grid-span-3 display-none-small">
              <span className="font-size-9 font-family-system font-weight-semibold shade-30 padding-horizontal-16">
                <FormattedMessage
                  id="workflows.detail.similar"
                  defaultMessage="More like this"
                />
              </span>
            </div>
            <div className="grid-span-9 grid-12 grid-gap-24 grid-gap-12-small grid-span-12-small">
              {similarWorkflows.map((similarWorkflow) => (
                <WorkflowCard
                  key={similarWorkflow.slug.current}
                  slug={similarWorkflow.slug.current}
                  title={similarWorkflow.title}
                  estMinSaved={similarWorkflow.estMinSaved}
                  sources={similarWorkflow.sources}
                  className="grid-span-4 grid-span-6-medium grid-span-12-small"
                />
              ))}
            </div>
          </div>
        </Section>
      )}
    </SlitePage>
  )
}
